import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Linkedin, ArrowRight, Clock, Users, TrendingUp, Sparkles } from "lucide-react";

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  const beliefs = [
    { icon: <Clock className="w-5 h-5" />, text: "Save time" },
    { icon: <Users className="w-5 h-5" />, text: "Strengthen families" },
    { icon: <TrendingUp className="w-5 h-5" />, text: "Increase productivity" },
    { icon: <Sparkles className="w-5 h-5" />, text: "Deepen personal growth" }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            {/* Content */}
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">About</p>
              <h1 className="heading-1 mb-6">Hi, I'm Aaron DPenha.</h1>
              <div className="space-y-4 body-text">
                <p>
                  I build practical AI-driven apps that solve everyday problems — from productivity and 
                  learning to growth and food.
                </p>
                <p>
                  With a background in building products, leadership, and education technology, 
                  I focus on creating tools that are simple, powerful, and built for real-world use.
                </p>
              </div>
              
              <div className="mt-8">
                <a
                  href="https://www.linkedin.com/in/aarondpenha/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="about-linkedin-btn"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Beliefs Section */}
      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">I believe technology should help:</h2>
            
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {beliefs.map((belief, index) => (
                <motion.div
                  key={belief.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="feature-card flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 flex-shrink-0">
                    {belief.icon}
                  </div>
                  <span className="text-lg font-medium text-zinc-900">{belief.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center">
            <h2 className="heading-2 mb-6">The App Fam Mission</h2>
            <p className="body-lg mb-8">
              Through The App Fam, I'm building a portfolio of applications that blend 
              artificial intelligence with purpose.
            </p>
            <p className="body-text max-w-2xl mx-auto">
              Whether it's helping students and professionals organize their thoughts, 
              families cook healthier meals, or individuals grow spiritually — 
              each app is designed to make life better.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-zinc-900">
        <div className="container-main text-center">
          <motion.div {...fadeInUp}>
            <h2 className="heading-3 text-white mb-4">Let's connect.</h2>
            <p className="text-lg text-zinc-400 mb-8 max-w-xl mx-auto">
              Have questions or ideas? I'd love to hear from you.
            </p>
            <Link
              to="/contact"
              className="h-12 px-8 rounded-full bg-white text-zinc-900 font-medium text-sm hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
              data-testid="about-contact-btn"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
